// THE IMPORT CONTROL EDITOR'S STATE, AS ONE VALUE (code review D-13,
// D-284).
//
// `EditableInvoiceForm` keeps this in one place and changes it only through
// `editorReducer`. It holds the latest value in a ref as well as rendering it,
// so a save started by a timer, a blur or a pick reads what is on screen NOW,
// never what a render's closure saw. Six mirror refs and a commit-tick effect
// did that job before, one per field, and each was a place for the two copies
// to disagree.
//
// No React and no I/O here: every rule below is a plain function, tested on its
// own in `app/mock/tests/invoice-editor-state.test.ts`.
import { CHARGE_GROUP_NAME, realLines } from '@ap/shared'
import type { Invoice, IntakeSaveResult, InvoiceLineItem, LineItemTransportationDetail } from './types'
import { money2 } from './format'
import {
  chargeKindOfPlaceholder,
  chargeMemberIds,
  groupsFrom,
  headerStateFrom,
  lineChangeBaselineFrom,
  lineItemStateFrom,
  rowTotal,
  rowKey,
  type GroupState,
  type HeaderState,
  type LineItemState,
} from './intakeSaveBody'
import { applyToRows, type BulkChoice, type CombineInput } from './lineItemBulk'
import { lineWriteSignature } from './saveOnBlur'

/**
 * One line's Additional Detail as the editor holds them (D-44, D-154). A
 * detail row is its own resource with its own `row_version` (`0` = "believed
 * not to exist yet"), written through its own route, never through the intake
 * save. The category labels are display text, refreshed the moment a pick is
 * made.
 */
export interface LineDetailState {
  marketingCategoryId: string | null
  marketingCategory: string | null
  marketingCategoryContext: string | null
  marketingRowVersion: number
  /** Fleet Maintenance's own detail (D-381), shown read-only; `null` on every
      line that did not come from Fleet Maintenance. Nothing here writes it. */
  transportation: LineItemTransportationDetail | null
}

export function detailStateFrom(c: InvoiceLineItem | undefined): LineDetailState {
  const md = c?.marketing_detail
  const td = c?.transportation_detail
  return {
    marketingCategoryId: md?.category_id ?? null,
    marketingCategory: md?.category ?? null,
    marketingCategoryContext: md?.category_context ?? null,
    marketingRowVersion: md?.row_version ?? 0,
    transportation: td ?? null,
  }
}

export interface EditorState {
  header: HeaderState
  rows: readonly LineItemState[]
  /** Each line's Additional Detail, by line id. A line added this session has
      none until the save that creates it answers. */
  details: Readonly<Record<string, LineDetailState>>
  /** Saved lines the reviewer removed, sent with the next save (D-164). */
  removedIds: readonly string[]
  /** Edits on screen that no save has stored yet. */
  dirty: boolean
  /** Bumped by every edit. A save compares it with the value it started from:
      an edit typed while the request was out is not in what was stored, so
      the save must not clear `dirty` or release (QA Q11). */
  editSeq: number
  /** The invoice's concurrency counter, sent with every save. */
  rowVersion: number
  /** Each stored line's counter, by id — replaced by every save's answer, so a
      save queued behind another never sends a superseded one. */
  lineVersions: ReadonlyMap<string, number>
  /** Each stored line's last-saved write signature, by id: a save sends only
      the lines whose signature moved (C25). */
  baseline: ReadonlyMap<string, string>
  /** What is stored, after another person's save collided with this one. */
  serverRow: Invoice | null
  /** The last temporary id handed to an added line. */
  newIds: number
  /** The groups on screen (D-399): what the next save makes stored. */
  groups: readonly GroupState[]
  /** The groups as last stored. A save sends the difference (`groupOperations`). */
  groupBaseline: readonly GroupState[]
  /** The last temporary key handed to a group made this session. */
  newGroupIds: number
}

export function initialEditorState(invoice: Invoice, lineItems: readonly InvoiceLineItem[]): EditorState {
  // The editor's rows are the REAL LINES (D-403); a group row is drawn from
  // its lines, never edited as a line.
  const items = realLines(lineItems)
  const groups = groupsFrom(lineItems)
  return {
    header: headerStateFrom(invoice),
    rows: items.map(lineItemStateFrom),
    details: Object.fromEntries(items.map((c) => [c.id, detailStateFrom(c)])),
    removedIds: [],
    dirty: false,
    editSeq: 0,
    rowVersion: invoice.row_version,
    lineVersions: new Map(lineItems.map((c) => [c.id, c.row_version])),
    baseline: lineChangeBaselineFrom(lineItems),
    serverRow: null,
    newIds: 0,
    groups,
    groupBaseline: groups,
    newGroupIds: 0,
  }
}

/**
 * G-21 ON SCREEN: a group left with one line dissolves, and with none is gone.
 * The server does the same after the save (`refreshGroups`). A CHARGE GROUP
 * STAYS (D-420): the invoice's Tax and Shipping groups hold one line, or none.
 */
function withoutLoneGroups(groups: readonly GroupState[]): GroupState[] {
  // A group of one stays (owner, 2026-10-28 — D-423); one of none goes, unless
  // it is the Tax or Shipping group (D-420).
  return groups.filter((g) => g.chargeKind !== undefined || g.members.length >= 1)
}

/**
 * A LINE ENTERING A CHARGE GROUP loses its Account and Department (D-420), as
 * the server clears them, so the next save sends no value the server refuses
 * on a charge line. Its Marketing Category the server clears on its own route.
 */
function withoutCoding(rows: readonly LineItemState[], ids: ReadonlySet<string>): LineItemState[] {
  return rows.map((r) =>
    ids.has(r.lineItemId)
      ? { ...r, accountId: undefined, accountLabel: undefined, departmentId: undefined, departmentLabel: undefined, departmentPicked: false }
      : r,
  )
}

/** The groups with `ids` taken out of whichever group held them. */
function takeOut(groups: readonly GroupState[], ids: ReadonlySet<string>): GroupState[] {
  return groups.map((g) => (g.members.some((m) => ids.has(m)) ? { ...g, members: g.members.filter((m) => !ids.has(m)) } : g))
}

/**
 * LINES REMOVED — one by its trash button, or every checked one by the bulk
 * row's Delete (D-407): each leaves the rows, a stored one is sent as a
 * removal (a soft delete, D-164), and each leaves its group, so a group of one
 * dissolves and a group of none is retired (G-21).
 */
function removeRows(state: EditorState, ids: ReadonlySet<string>): EditorState {
  const stored = state.rows.filter((r) => ids.has(r.lineItemId) && !r.isNew).map((r) => r.lineItemId)
  return edited(state, {
    rows: state.rows.filter((r) => !ids.has(r.lineItemId)),
    removedIds: [...state.removedIds, ...stored.filter((id) => !state.removedIds.includes(id))],
    groups: withoutLoneGroups(takeOut(state.groups, ids)),
  })
}

/** The rows' order, for a group's members: by line number, as the table shows them. */
function inRowOrder(rows: readonly LineItemState[], ids: Iterable<string>): string[] {
  const wanted = new Set(ids)
  return rows.filter((r) => wanted.has(r.lineItemId)).map((r) => r.lineItemId)
}

/** What one save sent, frozen when it started. */
export interface SaveSnapshot {
  header: HeaderState
  rows: readonly LineItemState[]
  removedIds: readonly string[]
  editSeq: number
  /** The rows the save wrote (`changedLineIds`). */
  changedIds: ReadonlySet<string>
  /** The groups on screen when it started (D-399). */
  groups?: readonly GroupState[]
}

export type EditorAction =
  | { type: 'header'; patch: Partial<HeaderState> }
  | { type: 'row'; id: string; patch: Partial<LineItemState> }
  /** Price or Quantity typed: the row's Total follows (D-4, code review D-02). */
  | { type: 'amounts'; id: string; price?: string; quantity?: string }
  | { type: 'addRow' }
  | { type: 'removeRow'; id: string }
  /** Delete in the bulk row (D-407): every one of them, as `removeRow` removes one. */
  | { type: 'removeRows'; ids: ReadonlySet<string> }
  | { type: 'applyToRows'; ids: ReadonlySet<string>; choice: BulkChoice }
  /** Combine makes a group of the lines (D-399, G-20). */
  | { type: 'combine'; ids: ReadonlySet<string>; input: CombineInput }
  /** Ungroup, or a line dragged out of its group: the lines stand alone. */
  | { type: 'removeFromGroup'; ids: ReadonlySet<string> }
  /** Move to Existing Group, or a line dragged onto one: each keeps its own values (G-20). */
  | { type: 'moveToGroup'; ids: ReadonlySet<string>; groupKey: string }
  | { type: 'renameGroup'; groupKey: string; name: string }
  /** A group row removed: its lines stand alone (review, major). */
  | { type: 'ungroup'; groupKey: string }
  /** A Marketing Category chosen. Saved on its own route, so it is
      not an edit of the intake save's. */
  | { type: 'detail'; id: string; patch: Partial<LineDetailState> }
  | { type: 'detailSaved'; id: string; lineItem: InvoiceLineItem }
  | { type: 'saved'; sent: SaveSnapshot; result: IntakeSaveResult; focused: ReadonlySet<string> }
  /** Take the invoice's counter from a 409, with what is stored when it was
      somebody else's write, or none when it was this form's own. */
  | { type: 'conflict'; rowVersion: number; serverRow: Invoice | null }
  /** The invoice left this step while the reviewer typed: what they typed
      since can no longer be stored here, so nothing is pending. */
  | { type: 'discard' }

/** Every action an edit: marks the form dirty and bumps the counter. */
function edited(state: EditorState, next: Partial<EditorState>): EditorState {
  return { ...state, ...next, dirty: true, editSeq: state.editSeq + 1 }
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'header':
      return edited(state, { header: { ...state.header, ...action.patch } })
    case 'row':
      return edited(state, {
        rows: state.rows.map((r) => (r.lineItemId === action.id ? { ...r, ...action.patch } : r)),
      })
    case 'amounts':
      return edited(state, {
        rows: state.rows.map((r) => {
          if (r.lineItemId !== action.id) return r
          const price = action.price ?? r.price
          const quantity = action.quantity ?? r.quantity
          return { ...r, price, quantity, total: rowTotal(r, price, quantity) }
        }),
      })
    case 'addRow': {
      const newIds = state.newIds + 1
      // Past every group row's number too (D-396): a group row takes the next
      // number never used on the invoice, so the lines alone would offer it again.
      const groupNumbers = [...state.groups, ...state.groupBaseline].map((g) => g.sequence ?? 0)
      const sequence = Math.max(0, ...groupNumbers, ...state.rows.map((r) => r.sequence ?? 0)) + 1
      return edited(state, {
        newIds,
        rows: [
          ...state.rows,
          { lineItemId: `new-${newIds}`, clientKey: `new-${newIds}`, sequence, description: '', quantity: '', price: '', total: '', isNew: true },
        ],
      })
    }
    case 'removeRow':
      return removeRows(state, new Set([action.id]))
    case 'removeRows':
      return removeRows(state, action.ids)
    case 'applyToRows': {
      // A charge line takes no Account or Department (D-420).
      const charges = chargeMemberIds(state.groups)
      const ids = new Set([...action.ids].filter((id) => !charges.has(id)))
      return edited(state, { rows: applyToRows(state.rows, ids, action.choice) })
    }
    case 'combine': {
      // COMBINE MAKES A GROUP (D-399): the lines stay, each taken out of any
      // group it was in, under a new group the next save creates. A value
      // chosen in the bulk row is written to every line (G-7).
      const newGroupIds = state.newGroupIds + 1
      const members = inRowOrder(state.rows, action.ids)
      const group: GroupState = { key: `group-new-${newGroupIds}`, name: action.input.description.trim(), members }
      const rows = action.input.choice ? applyToRows(state.rows, action.ids, action.input.choice) : state.rows
      return edited(state, {
        newGroupIds,
        rows,
        groups: withoutLoneGroups([...takeOut(state.groups, action.ids), group]),
      })
    }
    case 'removeFromGroup':
      return edited(state, { groups: withoutLoneGroups(takeOut(state.groups, action.ids)) })
    case 'moveToGroup': {
      // ONTO A CHARGE GROUP THE INVOICE DOES NOT HOLD YET (D-420): the
      // placeholder's key names its kind, and the group is made under that key.
      const placeholder = chargeKindOfPlaceholder(action.groupKey)
      const groups =
        placeholder && !state.groups.some((g) => g.key === action.groupKey)
          ? [...state.groups, { key: action.groupKey, name: CHARGE_GROUP_NAME[placeholder], chargeKind: placeholder, members: [] }]
          : state.groups
      const target = groups.find((g) => g.key === action.groupKey)
      if (!target) return state
      const out = takeOut(groups, action.ids)
      return edited(state, {
        rows: target.chargeKind ? withoutCoding(state.rows, action.ids) : state.rows,
        groups: withoutLoneGroups(
          out.map((g) => (g.key === action.groupKey ? { ...g, members: inRowOrder(state.rows, [...g.members, ...action.ids]) } : g)),
        ),
      })
    }
    case 'renameGroup':
      // A charge group's name is fixed (D-420).
      return edited(state, {
        groups: state.groups.map((g) => (g.key === action.groupKey && !g.chargeKind ? { ...g, name: action.name } : g)),
      })
    case 'ungroup':
      // A charge group is never removed (D-420).
      return edited(state, { groups: state.groups.filter((g) => g.key !== action.groupKey || g.chargeKind !== undefined) })
    case 'detail': {
      const d = state.details[action.id] ?? detailStateFrom(undefined)
      return { ...state, details: { ...state.details, [action.id]: { ...d, ...action.patch } } }
    }
    case 'detailSaved':
      return { ...state, details: { ...state.details, [action.id]: detailStateFrom(action.lineItem) } }
    case 'saved':
      return adoptSave(state, action.sent, action.result, action.focused)
    case 'conflict':
      return { ...state, rowVersion: action.rowVersion, serverRow: action.serverRow }
    case 'discard':
      return { ...state, dirty: false }
  }
}

/**
 * Pair each row a save SENT with the stored line it became. A row that went
 * with an id keeps it. A row that went in new is paired among the lines this
 * save created and nothing else: the stored lines no sent row names and no
 * sent removal names. By its line number first; a created line whose number
 * the server moved (a combined line skips a number a removed line still
 * reserves) is paired with the new rows left over, in order. Until
 * 2026-10-03 the pool also held a line removed while the save was out, and a
 * row added since took that line's id (code review of D-284).
 */
export function pairSentToStored(
  sent: readonly LineItemState[],
  stored: readonly InvoiceLineItem[],
  removed: readonly string[] = [],
): Map<string, InvoiceLineItem> {
  const named = new Set([...sent.filter((r) => !r.isNew).map((r) => r.lineItemId), ...removed])
  const created = stored.filter((c) => !named.has(c.id))
  const out = new Map<string, InvoiceLineItem>()
  for (const r of sent) {
    if (r.isNew) continue
    const match = stored.find((c) => c.id === r.lineItemId)
    if (match) out.set(r.lineItemId, match)
  }
  const fresh = sent.filter((r) => r.isNew)
  const unpaired: LineItemState[] = []
  for (const r of fresh) {
    const at = r.sequence == null ? -1 : created.findIndex((c) => c.line_number === r.sequence)
    if (at < 0) unpaired.push(r)
    else out.set(r.lineItemId, created.splice(at, 1)[0]!)
  }
  for (const r of unpaired) {
    const next = created.shift()
    if (next) out.set(r.lineItemId, next)
  }
  return out
}

/**
 * A SAVE'S ANSWER, TAKEN BACK. The counters always: the invoice's, every
 * stored line's, and the baseline of each line this save wrote. Then one of
 * two things:
 *
 * - NOTHING TYPED SINCE IT STARTED: the stored values replace what is on
 *   screen — except in a field that has focus right now, whose text is
 *   already exactly what was sent, so a reseed would only move the caret
 *   (`money2` turning "50" into "50.00" under it, C21).
 * - SOMETHING TYPED SINCE: every value on screen stays, the form stays dirty,
 *   and only identity is taken — a line this save created now has its id, so
 *   the next save updates it rather than creating a second copy.
 *
 * Either way the removals this save carried are done, and one made since is not.
 */
function adoptSave(
  state: EditorState,
  sent: SaveSnapshot,
  result: IntakeSaveResult,
  focused: ReadonlySet<string>,
): EditorState {
  const clean = state.editSeq === sent.editSeq
  const answered = result.line_items
  // The real lines alone are paired with rows: a group row the save created is
  // never a line this form sent (D-403).
  const stored = answered ? realLines(answered) : undefined
  // The pairing is the SENT rows' (code review of D-284): a row added or a line
  // removed while the save was out took no part in it.
  const pairing = stored ? pairSentToStored(sent.rows, stored, sent.removedIds) : new Map<string, InvoiceLineItem>()
  let details = state.details
  let lineVersions = state.lineVersions
  let baseline = state.baseline
  if (stored) {
    // A line this save created has detail rows now — a combined line carries
    // its lines' agreed categories (D-230).
    const fresh = stored.filter((c) => !(c.id in details))
    if (fresh.length > 0) details = { ...details, ...Object.fromEntries(fresh.map((c) => [c.id, detailStateFrom(c)])) }
    lineVersions = new Map([...lineVersions, ...stored.map((c) => [c.id, c.row_version] as const)])
    const nextBaseline = new Map(baseline)
    for (const r of sent.rows) {
      if (!sent.changedIds.has(r.lineItemId)) continue
      const line = pairing.get(r.lineItemId)
      if (line) nextBaseline.set(line.id, lineWriteSignature(r))
    }
    baseline = nextBaseline
  }
  const removedIds = state.removedIds.filter((id) => !sent.removedIds.includes(id))
  // THE GROUPS AS STORED NOW are the baseline the next save is measured from.
  const storedGroups = answered ? groupsFrom(answered) : state.groupBaseline
  const base = {
    ...state,
    details,
    lineVersions,
    baseline,
    removedIds,
    groupBaseline: storedGroups,
    rowVersion: result.invoice.row_version,
    serverRow: null,
  }
  if (!clean) {
    return stored
      ? { ...base, rows: adoptLineIdentity(state.rows, pairing, stored), groups: adoptGroupIdentity(state.groups, pairing, storedGroups) }
      : base
  }
  return {
    ...base,
    dirty: false,
    header: reseedHeader(state.header, result.invoice, focused),
    rows: stored ? reseedRows(state.rows, pairing, focused) : state.rows,
    // A group made this session keeps its key on screen, so it does not
    // collapse under the reviewer when the save gives it its stored id.
    groups: answered ? keepGroupKeys(adoptGroupIdentity(state.groups, pairing, storedGroups), storedGroups) : state.groups,
  }
}

/** The stored groups, each under the key it had on screen. */
function keepGroupKeys(onScreen: readonly GroupState[], storedGroups: readonly GroupState[]): GroupState[] {
  return storedGroups.map((s) => ({ ...s, key: onScreen.find((g) => g.storedId === s.storedId)?.key ?? s.key }))
}

/**
 * The groups on screen, with the ids a save gave them: each member that went
 * out new takes its stored id, and each group made this session takes the
 * stored group holding exactly its lines. Its key on screen is kept.
 */
function adoptGroupIdentity(
  groups: readonly GroupState[],
  pairing: ReadonlyMap<string, InvoiceLineItem>,
  storedGroups: readonly GroupState[],
): GroupState[] {
  const sameMembers = (a: readonly string[], b: readonly string[]) =>
    a.length === b.length && a.every((id) => b.includes(id))
  return groups.map((g) => {
    const members = g.members.map((id) => pairing.get(id)?.id ?? id)
    if (g.storedId) {
      const stored = storedGroups.find((s) => s.storedId === g.storedId)
      return { ...g, members, sequence: stored?.sequence ?? g.sequence }
    }
    // A charge group made this session is the stored group of its kind (D-420).
    const stored = g.chargeKind
      ? storedGroups.find((s) => s.chargeKind === g.chargeKind)
      : storedGroups.find((s) => !s.chargeKind && sameMembers(s.members, members))
    return stored ? { ...g, members, storedId: stored.storedId, sequence: stored.sequence } : { ...g, members }
  })
}

/** The header as stored, keeping the text of a focused field. */
function reseedHeader(prev: HeaderState, saved: Invoice, focused: ReadonlySet<string>): HeaderState {
  const keep = (key: string) => focused.has(key)
  const dateDefault = (saved.invoice_date ?? '').slice(0, 10)
  return {
    externalinvoiceid: keep('externalinvoiceid') ? prev.externalinvoiceid : (saved.external_invoice_id ?? dateDefault),
    // The seeded default, kept only while the server still holds no number; a
    // field being typed in keeps what it had.
    ...(keep('externalinvoiceid')
      ? prev.externalInvoiceIdDefault !== undefined
        ? { externalInvoiceIdDefault: prev.externalInvoiceIdDefault }
        : {}
      : saved.external_invoice_id == null
        ? { externalInvoiceIdDefault: dateDefault }
        : {}),
    date: keep('date') ? prev.date : saved.invoice_date ? saved.invoice_date.slice(0, 10) : '',
    subtotal: saved.subtotal != null ? money2(String(saved.subtotal)) : '',
    tax: keep('tax') ? prev.tax : saved.tax != null ? money2(String(saved.tax)) : '0.00',
    shipping: keep('shipping') ? prev.shipping : saved.shipping != null ? money2(String(saved.shipping)) : '0.00',
    total: keep('total') ? prev.total : saved.total != null ? money2(String(saved.total)) : '',
    currency: saved.currency ?? 'USD',
    vendorId: saved.vendor_id || undefined,
    vendorName: saved.vendor_name || undefined,
    // The invoice's own company, which the save writes (code review B-05).
    companyId: saved.company_id || undefined,
    companyName: saved.company_name || undefined,
    manualEntry: saved.manual_entry_requested ?? false,
  }
}

/** Every row as stored, keeping the text of a focused Description, Quantity or
    Price, and its key on screen. */
function reseedRows(
  rows: readonly LineItemState[],
  pairing: ReadonlyMap<string, InvoiceLineItem>,
  focused: ReadonlySet<string>,
): LineItemState[] {
  return rows.map((prev) => {
    const line = pairing.get(prev.lineItemId)
    if (!line) return prev
    const key = rowKey(prev)
    const fresh = { ...lineItemStateFrom(line), clientKey: key }
    const keepDescription = focused.has(`description:${key}`)
    const keepQuantity = focused.has(`quantity:${key}`)
    const keepPrice = focused.has(`price:${key}`)
    if (!keepDescription && !keepQuantity && !keepPrice) return fresh
    const description = keepDescription ? prev.description : fresh.description
    const quantity = keepQuantity ? prev.quantity : fresh.quantity
    const price = keepPrice ? prev.price : fresh.price
    return { ...fresh, description, quantity, price, total: rowTotal(fresh, price, quantity) }
  })
}

/** The stored lines' ids and counters, every value on screen kept. Only a row
    that went out new takes an id; a row added since stays new. */
export function adoptLineIdentity(
  rows: readonly LineItemState[],
  pairing: ReadonlyMap<string, InvoiceLineItem>,
  stored: readonly InvoiceLineItem[],
): LineItemState[] {
  return rows.map((r) => {
    if (r.isNew) {
      const c = pairing.get(r.lineItemId)
      return c ? { ...r, clientKey: rowKey(r), lineItemId: c.id, rowVersion: c.row_version, isNew: false } : r
    }
    const c = stored.find((x) => x.id === r.lineItemId)
    return c ? { ...r, rowVersion: c.row_version } : r
  })
}

/**
 * THE EDITOR'S LINES AND GROUPS AS THE SHARED ROW SETS READ THEM (D-420): a
 * group row per group, its kind on a charge group, and each line under its
 * group's key — so `chargeSpread`, the CSV's own spread, reads what is on screen.
 */
export function editorGroupedRows(
  rows: readonly LineItemState[],
  groups: readonly GroupState[],
): { id: string; is_active: boolean; line_kind: 'item' | 'group'; group_id: string | null; charge_kind: GroupState['chargeKind'] | null; total?: string }[] {
  const groupOf = new Map<string, string>()
  for (const g of groups) for (const m of g.members) groupOf.set(m, g.key)
  return [
    ...groups.map((g) => ({ id: g.key, is_active: true, line_kind: 'group' as const, group_id: null, charge_kind: g.chargeKind ?? null })),
    ...rows.map((r) => ({
      id: r.lineItemId,
      is_active: true,
      line_kind: 'item' as const,
      group_id: groupOf.get(r.lineItemId) ?? null,
      charge_kind: null,
      total: r.total,
    })),
  ]
}
